import React, { useEffect, useRef, useState } from "react";
import Marquee from "react-fast-marquee";
import api, { formatNumber } from "@/lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function TVMode() {
  const [rates, setRates] = useState(null);
  const [site, setSite] = useState(null);
  const [now, setNow] = useState(new Date());
  const [flashMap, setFlashMap] = useState({}); // key -> "up"|"down"
  const prevRef = useRef({});

  useEffect(() => {
    document.body.classList.add("tv-mode-body");
    document.documentElement.style.background = "#050505";
    return () => {
      document.body.classList.remove("tv-mode-body");
      document.documentElement.style.background = "";
    };
  }, []);

  useEffect(() => { api.get("/settings/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/rates");
        if (!alive) return;
        const all = [...(data.currencies || []), ...(data.gold || []), ...(data.silver || [])];
        const newFlash = {};
        all.forEach((r) => {
          const key = r.code || r.key;
          const prev = prevRef.current[key];
          if (prev != null) {
            if (r.display_sell > prev) newFlash[key] = "up";
            else if (r.display_sell < prev) newFlash[key] = "down";
          }
          prevRef.current[key] = r.display_sell;
        });
        if (Object.keys(newFlash).length) {
          setFlashMap((m) => ({ ...m, ...newFlash }));
          setTimeout(() => setFlashMap({}), 850);
        }
        setRates(data);
      } catch {}
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!rates) return <div data-testid="tv-loading" className="min-h-screen bg-obsidian text-white flex items-center justify-center text-2xl font-finance">UZUNGÖL · CONNECTING...</div>;

  const tickerItems = [
    ...(rates.currencies || []).filter((c) => ["USD", "EUR", "GBP", "SAR", "AED", "QAR"].includes(c.code)).map((c) => ({ label: c.code, value: c.display_sell, key: c.code })),
    ...(rates.gold || []).map((g) => ({ label: g.name_tr, value: g.display_sell, key: g.key })),
    ...(rates.silver || []).map((s) => ({ label: s.name_tr, value: s.display_sell, key: s.key })),
  ];

  const lastUpdate = rates.fetched_at ? new Date(rates.fetched_at) : new Date();

  return (
    <div data-testid="tv-mode-page" className="min-h-screen bg-obsidian text-white flex flex-col">
      {/* Top Ticker */}
      <div data-testid="tv-ticker" className="h-12 bg-[#0A0A0A] border-b border-[#27272A] flex items-center overflow-hidden tv-ticker-mask">
        <Marquee speed={55} gradient={false}>
          {tickerItems.map((it, i) => (
            <span key={i} className="font-finance text-base px-8 inline-flex items-center gap-3">
              <span className="text-[#D4AF37] uppercase tracking-[0.2em]">{it.label}</span>
              <span className="text-white">{formatNumber(it.value, 2)}</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Header Bar */}
      <div className="px-8 py-4 flex justify-between items-center border-b border-[#27272A]">
        <div className="flex items-center gap-4">
          <span className="inline-block w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#10B981]">CANLI · LIVE</span>
          {site?.logo_url ? <img src={site.logo_url} alt="logo" className="h-10 w-10 object-cover ml-4 rounded-sm" /> : null}
          <span className="font-heading text-2xl">Uzungöl Kuyumculuk</span>
        </div>
        <div data-testid="tv-time" className="text-right font-finance">
          <div className="text-3xl tracking-wider">{now.toLocaleTimeString("tr-TR", { hour12: false })}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">{now.toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
          <div className="text-[10px] text-[#71717A] mt-0.5">Son Güncelleme: {lastUpdate.toLocaleTimeString("tr-TR")}</div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 grid grid-cols-3 gap-4 p-6">
        <TVPanel testid="tv-currencies" title="DÖVİZ" items={rates.currencies?.filter((c) => ["USD", "EUR", "GBP", "SAR", "AED", "QAR"].includes(c.code))} flashMap={flashMap} keyField="code" />
        <TVPanel testid="tv-gold" title="ALTIN" items={rates.gold} flashMap={flashMap} keyField="key" highlight />
        <TVPanel testid="tv-silver" title="GÜMÜŞ" items={rates.silver} flashMap={flashMap} keyField="key" />
      </div>

      {/* Footer disclaimer */}
      <div className="px-8 py-3 border-t border-[#27272A] text-[10px] uppercase tracking-[0.25em] text-[#71717A] flex justify-between">
        <span>Fiyatlar bilgi amaçlıdır</span>
        <span>UZUNGÖL · TRABZON</span>
      </div>
    </div>
  );
}

function TVPanel({ testid, title, items, flashMap, keyField, highlight }) {
  return (
    <div data-testid={testid} className={`bg-[#0F0F11] border ${highlight ? "border-[#D4AF37]/40" : "border-[#27272A]"} flex flex-col`}>
      <div className={`px-6 py-4 border-b ${highlight ? "border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/10 to-transparent" : "border-[#27272A]"}`}>
        <h2 className={`font-heading text-3xl ${highlight ? "text-[#D4AF37]" : "text-white"}`}>{title}</h2>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] px-6 py-2 text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] border-b border-[#27272A]">
        <span>Ürün</span>
        <span className="text-right pr-8">Alış</span>
        <span className="text-right">Satış</span>
      </div>
      <div className="flex-1 divide-y divide-[#1f1f22]">
        {(items || []).map((r) => {
          const key = r[keyField];
          const flash = flashMap[key];
          return (
            <motion.div
              key={key}
              data-testid={`tv-row-${key}`}
              animate={flash === "up" ? { backgroundColor: ["rgba(16,185,129,0.25)", "rgba(0,0,0,0)"] } : flash === "down" ? { backgroundColor: ["rgba(239,68,68,0.25)", "rgba(0,0,0,0)"] } : { backgroundColor: "rgba(0,0,0,0)" }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-[1fr_auto_auto] items-center px-6 py-3"
            >
              <span className="text-sm text-white truncate">{r.name_tr || r.code}</span>
              <span className="font-finance text-right pr-8 text-[#A1A1AA]">{formatNumber(r.display_buy, 2)}</span>
              <span className="font-finance text-right text-lg flex items-center justify-end gap-2">
                <span className={flash === "up" ? "text-[#10B981]" : flash === "down" ? "text-[#EF4444]" : "text-white"}>{formatNumber(r.display_sell, 2)}</span>
                {flash === "up" && <TrendingUp size={14} className="text-[#10B981]" />}
                {flash === "down" && <TrendingDown size={14} className="text-[#EF4444]" />}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
